/**
 * Load one brand's Shopify variant lookup table from Dato.
 *
 * Why this exists:
 * We do NOT load every brand's huge variant list with style steps.
 * When we know the make (Honda, Toyota, …), we fetch only that brand's map.
 *
 * Dato setup:
 * - Same model as Upholstery Steps V2
 * - Record key must be: "{make}-shopify-variants"  e.g. "honda-shopify-variants"
 * - JSON field `options` looks like:
 *   { "shopifyVariants": [ { m3Code, type, shopifyVariantId, shopifyProductId, title } ] }
 * - Factory match rows may also have
 *   "outOfStock": { shopifyVariantId, shopifyProductId } for the special-order product.
 *
 * Who calls this:
 * - loadUpholsteryVehicleSelection (when a car is picked)
 * - useInventoryOrderForm (when finalize page opens / submits)
 * - form submit again (uses cache if the same make was already loaded)
 */

import { request } from "@data/datoRequest"
import { upholsteryShopifyVariantsByMakeQuery } from "@views/Upholstery/graphql"
import type {
  UpholsteryShopifyOutOfStockIds,
  UpholsteryShopifyVariantEntry,
  UpholsteryShopifyVariantType,
} from "@views/Upholstery/types"
import { useShopifyVariantsByMakeStore } from "../../store/shopify-variants-by-make-store"

/**
 * Normalize a make name for keys: "Honda" → "honda", "Land Rover" → "land-rover".
 */
export function normalizeMakeKey(make: string): string {
  return make.trim().toLowerCase().replace(/\s+/g, "-")
}

/**
 * Build the Dato record key for a make.
 * Example: makeToShopifyVariantsKey("Honda") → "honda-shopify-variants"
 */
export function makeToShopifyVariantsKey(make: string): string {
  return `${normalizeMakeKey(make)}-shopify-variants`
}

/**
 * Parse Dato `options` whether it comes back as an object or a JSON string.
 */
function parseOptionsJson(raw: unknown): Record<string, unknown> | null {
  if (raw == null) return null
  if (typeof raw === "string") {
    try {
      const parsed = JSON.parse(raw) as unknown
      if (parsed && typeof parsed === "object" && !Array.isArray(parsed)) {
        return parsed as Record<string, unknown>
      }
      return null
    } catch {
      return null
    }
  }
  if (typeof raw === "object" && !Array.isArray(raw)) {
    return raw as Record<string, unknown>
  }
  return null
}

/**
 * Pull the array of variant rows out of options JSON.
 * Supports { shopifyVariants: [...] } and a double-wrapped mistake shape.
 */
function variantRowsFromOptions(options: unknown): unknown[] {
  const parsed = parseOptionsJson(options)
  if (!parsed) return []

  let rows: unknown = parsed.shopifyVariants ?? parsed.variants

  // Support accidental double-wrap: { shopifyVariants: { shopifyVariants: [...] } }
  if (!Array.isArray(rows) && rows && typeof rows === "object") {
    const inner = rows as Record<string, unknown>
    const nested = inner.shopifyVariants ?? inner.variants
    if (Array.isArray(nested)) rows = nested
  }

  return Array.isArray(rows) ? rows : []
}

/**
 * Turn Dato's type string into our app type.
 * Accepts "custom", "factoryMatch", or short "factory".
 */
function normalizeVariantType(
  raw: unknown
): UpholsteryShopifyVariantType | null {
  const t = String(raw ?? "")
    .trim()
    .toLowerCase()
    .replace(/\s+/g, "")
  if (t === "custom") return "custom"
  if (t === "factorymatch" || t === "factory") return "factoryMatch"
  return null
}

/**
 * Read the nested `outOfStock` ids from a factory match row.
 * Returns undefined when the block is missing or has no variant id.
 */
function normalizeOutOfStockIds(
  raw: unknown
): UpholsteryShopifyOutOfStockIds | undefined {
  if (!raw || typeof raw !== "object" || Array.isArray(raw)) return undefined
  const r = raw as Record<string, unknown>
  const shopifyVariantId =
    r.shopifyVariantId != null ? String(r.shopifyVariantId).trim() : ""
  if (!shopifyVariantId) return undefined
  const shopifyProductId =
    r.shopifyProductId != null ? String(r.shopifyProductId).trim() : ""
  return {
    shopifyVariantId,
    shopifyProductId: shopifyProductId || null,
  }
}

/**
 * Clean one raw Dato row into a typed entry.
 * Returns null if m3Code or type is missing (those rows are skipped).
 */
export function normalizeShopifyVariantEntry(
  row: unknown
): UpholsteryShopifyVariantEntry | null {
  if (!row || typeof row !== "object") return null
  const r = row as Record<string, unknown>
  const m3Code = String(r.m3Code ?? "").trim()
  const type = normalizeVariantType(r.type)
  if (!m3Code || !type) return null

  const shopifyVariantId =
    r.shopifyVariantId != null ? String(r.shopifyVariantId).trim() : ""
  const shopifyProductId =
    r.shopifyProductId != null ? String(r.shopifyProductId).trim() : ""
  const title = r.title != null ? String(r.title) : undefined
  const outOfStock =
    type === "factoryMatch" ? normalizeOutOfStockIds(r.outOfStock) : undefined

  return {
    m3Code,
    type,
    shopifyVariantId: shopifyVariantId || null,
    shopifyProductId: shopifyProductId || null,
    title,
    ...(outOfStock ? { outOfStock } : {}),
  }
}

/**
 * Turn the Dato `options` field into a clean list of variant entries.
 * Used by fetchShopifyVariantsByMake after the GraphQL response arrives.
 */
export function parseShopifyVariantsFromOptions(
  options: unknown
): UpholsteryShopifyVariantEntry[] {
  const out: UpholsteryShopifyVariantEntry[] = []
  for (const row of variantRowsFromOptions(options)) {
    const entry = normalizeShopifyVariantEntry(row)
    if (entry) out.push(entry)
  }
  return out
}

type RawShopifyVariantsRecord = {
  id?: string | null
  key?: string | null
  name?: string | null
  options?: unknown
}

/** In-flight fetches so two callers for the same make share one network request. */
const inflightByMake = new Map<
  string,
  Promise<UpholsteryShopifyVariantEntry[]>
>()

/**
 * Fetch the Shopify variant map for one make and store it in Zustand.
 *
 * Examples:
 * - fetchShopifyVariantsByMake("Honda") loads key "honda-shopify-variants"
 *
 * Behavior:
 * - Uses cache if that make was already loaded
 * - Fail soft: missing Dato record or network error → [] (empty list)
 *   Configurator still works; checkout later just won't find a mapping
 */
export async function fetchShopifyVariantsByMake(
  make: string
): Promise<UpholsteryShopifyVariantEntry[]> {
  const makeKey = normalizeMakeKey(make)
  if (!makeKey) {
    useShopifyVariantsByMakeStore.getState().actions.reset()
    return []
  }

  const recordKey = makeToShopifyVariantsKey(make)
  const cached = useShopifyVariantsByMakeStore.getState()
  if (
    cached.makeKey === makeKey &&
    cached.fetchedAt != null &&
    !cached.loading
  ) {
    return cached.entries
  }

  const existing = inflightByMake.get(makeKey)
  if (existing) return existing

  const { actions } = useShopifyVariantsByMakeStore.getState()
  actions.setLoading(true)

  const promise = (async () => {
    try {
      const response = await request({
        query: upholsteryShopifyVariantsByMakeQuery,
        variables: { key: recordKey },
      })

      const records = (response?.allUpholsterCarsSteps ??
        []) as RawShopifyVariantsRecord[]
      const record = records[0]
      const entries = parseShopifyVariantsFromOptions(record?.options)

      if (!record) {
        console.warn(
          `[Upholstery Shopify] No Dato record for key "${recordKey}". Checkout mapping will be empty for this make.`
        )
      } else if (entries.length === 0) {
        console.warn(
          `[Upholstery Shopify] Record "${recordKey}" has no usable shopifyVariants rows.`
        )
      }

      actions.setEntries({ makeKey, recordKey, entries })
      return entries
    } catch (error) {
      const message =
        error instanceof Error
          ? error.message
          : `[Upholstery Shopify] Failed to fetch variants for ${recordKey}`
      console.warn(message, error)
      // Fail soft: empty list so configurator still works; checkout fails soft later.
      actions.setEntries({ makeKey, recordKey, entries: [] })
      useShopifyVariantsByMakeStore.setState({ error: message })
      return []
    } finally {
      inflightByMake.delete(makeKey)
    }
  })()

  inflightByMake.set(makeKey, promise)
  return promise
}
